/**
 * Inbox Manager Store
 *
 * Zustand store for inbox interaction list and detail state management.
 * Mirrors the pattern established in the Customer Manager (Task 2558).
 *
 * Handles:
 * - Interaction list fetching, filtering (client-side), sorting
 * - Single selection (inspector) and multi-selection (bulk actions)
 * - Detail cache for thread messages and case notes
 * - Optimistic updates for read/unread/classify actions
 * - URL state serialization/hydration
 */

import { create } from 'zustand'
import {
  listInteractions,
  fetchThreadMessages,
  getInteractionAssets,
  enrichMessageAttachments,
  updateProcessingStatus as apiUpdateProcessingStatus,
  batchArchiveInteractions,
  updateCaseNotes,
  replyToMessage,
  listInboxes,
  listAutomations,
  executeAutomation,
} from './api'
import {
  getClassification,
  getCategory,
  getSentimentScore,
  getUrgencyRank,
} from './classification'
import type {
  Interaction,
  InteractionDirection,
  ProcessingStatus,
  InteractionDetail,
  InboxFilters,
  InboxPresetFilters,
  InboxSortState,
  InboxInspectorTab,
  InboxSource,
  Message,
  ReplyRequest,
  Automation,
  MessageAttachment,
} from './types'

// ── Store State ──────────────────────────────────────────────────────────────

interface InboxStoreState {
  // List state
  interactions: Interaction[]
  cursor: string | null
  total: number
  hasMore: boolean
  filters: InboxFilters
  sortState: InboxSortState

  // Selection state
  selectedIds: Set<string>
  selectedId: string | null

  // Detail state
  detailCache: Map<string, InteractionDetail>
  activeTab: InboxInspectorTab

  // Loading states
  listLoading: boolean
  listError: string | null
  loadingMore: boolean
  // "Load all matches" (Task 4053f): true while the store is sequentially
  // paging through every remaining server page for the active search.
  loadingAllMatches: boolean
  // True when loadAllMatches stopped because it hit its page/item safety cap
  // while the server still reported more pages.
  loadAllStoppedAtCap: boolean
  detailLoading: boolean
  detailError: string | null

  // Inbox sources (for filter facet)
  inboxSources: InboxSource[]
  inboxSourcesLoaded: boolean

  // Automations available for manual dispatch from the inspector
  // (workflows triggered by `interaction.received`).
  automations: Automation[]
  automationsLoaded: boolean
  automationsLoading: boolean
  automationsError: string | null

  // Last updated
  lastFetchedAt: number | null

  // Live new-item count (for live-merge banner)
  newItemCount: number
}

// ── Store Actions ────────────────────────────────────────────────────────────

interface InboxStoreActions {
  // List operations
  loadList(): Promise<void>
  loadMore(): Promise<void>
  /**
   * Sequentially page through every remaining server page for the current
   * filters (Task 4053f "Load all matches"). Hard-capped at 20 pages / 1000
   * loaded items so a pathological query cannot hammer the backend forever;
   * when the cap interrupts the walk, `loadAllStoppedAtCap` is set so the
   * UI can tell the operator the result set is still partial.
   */
  loadAllMatches(): Promise<void>
  setFilters(filters: InboxFilters): void
  /**
   * Apply a preset by merging its (preset-controlled) facets ON TOP of the
   * operator's current direction/type state. Direction and type are
   * preserved because they represent operator intent — *what the operator
   * is looking at* — not a triage signal the preset has authority over.
   *
   * Other facets the preset does NOT explicitly set are also preserved
   * (e.g. an active inbox_id selection is not silently cleared when the
   * operator selects "Critical urgency"). The `all` preset is the special
   * "clear non-operator filters" preset — selecting it resets every
   * preset-controlled facet while leaving direction/type intact.
   */
  applyPreset(presetFilters: InboxPresetFilters, options?: { resetOtherFacets?: boolean }): void
  setSort(sort: InboxSortState): void
  clearFilters(): void
  loadInboxSources(): Promise<void>

  // Automations
  loadAutomations(): Promise<void>
  triggerAutomation(interactionId: string, automationId: string): Promise<void>
  /**
   * Dispatch multiple interactions to a workflow with a 3-second stagger
   * between each send. Returns immediately so the UI can confirm the action
   * optimistically; actual API calls fire in the background.
   */
  bulkTriggerAutomation(ids: string[], automationId: string): Promise<void>

  // Selection
  selectInteraction(id: string | null): void
  toggleSelection(id: string): void
  selectAll(): void
  clearSelection(): void

  // Detail operations
  setActiveTab(tab: InboxInspectorTab): void
  loadInteractionDetail(id: string): Promise<void>
  /**
   * Fetch and cache the full thread for an interaction that is NOT present
   * in the active inbox list (e.g. a customer-history row in the inspector).
   * Reuses the same `detailCache` keyed by interaction id so subsequent
   * expansions read from cache and re-rendering is instant.
   *
   * Task 3201b: inbox-history drill-down.
   */
  loadHistoryInteractionDetail(interaction: Interaction): Promise<void>

  // Processing status operations
  setInteractionStatus(id: string, status: ProcessingStatus): Promise<void>
  bulkSetStatus(ids: string[], status: ProcessingStatus): Promise<void>

  // Bulk operations
  bulkArchive(ids: string[]): Promise<void>

  // Case notes
  saveCaseNotes(id: string, notes: string): Promise<void>

  // Reply
  reply(request: ReplyRequest): Promise<Message>

  // Live merge
  incrementNewItems(count?: number): void
  clearNewItems(): void

  // URL sync
  hydrateFromUrl(searchParams: URLSearchParams): void
  serializeToUrl(): URLSearchParams
}

type InboxStore = InboxStoreState & InboxStoreActions

// ── Initial State ────────────────────────────────────────────────────────────

const initialState: InboxStoreState = {
  interactions: [],
  cursor: null,
  total: 0,
  hasMore: false,
  // Inbox surface is email-only. App Store reviews are also stored as
  // interactions (Type: "review") but render on the Reviews page; keep
  // them out of this list by default so the inbox triage queue stays
  // about email triage.
  filters: { direction: 'inbound', type: ['email'] },
  sortState: { column: 'received_at', direction: 'desc' },
  selectedIds: new Set(),
  selectedId: null,
  detailCache: new Map(),
  activeTab: 'history',
  listLoading: false,
  listError: null,
  loadingMore: false,
  loadingAllMatches: false,
  loadAllStoppedAtCap: false,
  detailLoading: false,
  detailError: null,
  inboxSources: [],
  inboxSourcesLoaded: false,
  automations: [],
  automationsLoaded: false,
  automationsLoading: false,
  automationsError: null,
  lastFetchedAt: null,
  newItemCount: 0,
}

// ── Helper: Apply client-side filters ────────────────────────────────────────

function applyClientFilters(
  interactions: Interaction[],
  filters: InboxFilters
): Interaction[] {
  let result = interactions

  // Text search (subject, from, preview)
  if (filters.q) {
    const q = filters.q.toLowerCase()
    result = result.filter(
      (i) =>
        i.subject.toLowerCase().includes(q) ||
        i.from.toLowerCase().includes(q) ||
        i.from_name?.toLowerCase().includes(q) ||
        i.preview?.toLowerCase().includes(q)
    )
  }

  // Processing status filter
  if (filters.processing_status && filters.processing_status.length > 0) {
    result = result.filter((i) => filters.processing_status!.includes(i.processing_status))
  }

  // Direction filter
  if (filters.direction) {
    result = result.filter((i) => i.direction === filters.direction)
  }

  // Type filter — keeps reviews/emails from leaking across surfaces.
  if (filters.type && filters.type.length > 0) {
    result = result.filter((i) => !i.type || filters.type!.includes(i.type))
  }

  // Classification filter — operates on the canonical category string,
  // which may live on either the v3 structured Classification object or
  // (for legacy summary-view responses) directly as a plain string.
  if (filters.classification && filters.classification.length > 0) {
    result = result.filter((i) =>
      filters.classification!.includes(
        getCategory(i) as (typeof filters.classification)[number],
      ),
    )
  }

  // Inbox filter
  if (filters.inbox_id && filters.inbox_id.length > 0) {
    result = result.filter((i) => filters.inbox_id!.includes(i.inbox_id))
  }

  // ── v3 Classification facets ─────────────────────────────────────────
  //
  // Each filter excludes rows whose underlying field is missing. Operators
  // narrowing to "Critical" urgency don't want v2 rows (which lack the
  // field) leaking into the result.
  if (filters.urgency && filters.urgency.length > 0) {
    const targets = new Set(filters.urgency)
    result = result.filter((i) => {
      const u = getClassification(i)?.urgency
      return u !== undefined && targets.has(u)
    })
  }

  if (filters.required_action && filters.required_action.length > 0) {
    const targets = new Set(filters.required_action)
    result = result.filter((i) => {
      const a = getClassification(i)?.required_action
      return a !== undefined && targets.has(a)
    })
  }

  if (filters.customer_tone && filters.customer_tone.length > 0) {
    const targets = new Set(filters.customer_tone)
    result = result.filter((i) => {
      const t = getClassification(i)?.customer_tone
      return t !== undefined && targets.has(t)
    })
  }

  if (filters.related_entity_kind && filters.related_entity_kind.length > 0) {
    const targets = new Set(filters.related_entity_kind)
    result = result.filter((i) => {
      const k = getClassification(i)?.related_entity_kind
      return k !== undefined && targets.has(k)
    })
  }

  if (filters.needs_review === true) {
    result = result.filter((i) => getClassification(i)?.needs_review === true)
  }

  // Attachment filter
  if (filters.attachment !== undefined) {
    result = result.filter((i) => i.has_attachment === filters.attachment)
  }

  // Date range filters
  if (filters.received_from) {
    const fromTs = new Date(filters.received_from).getTime() / 1000
    result = result.filter((i) => i.received_at >= fromTs)
  }
  if (filters.received_to) {
    const toTs = new Date(filters.received_to).getTime() / 1000
    result = result.filter((i) => i.received_at <= toTs)
  }

  // ── Duplicate suppression (Task 3510 follow-up) ────────────────────────
  //
  // The communications backend stamps `parent_interaction_id` on every row
  // that was identified as a re-submission of recent content from the same
  // envelope sender (see services/communications/.../email_processor.go
  // Step 9.5). The parent stays in its normal state (e.g. `pending`); the
  // children are flipped to `processing_status: skipped` and linked back
  // via `parent_interaction_id`.
  //
  // The operator inbox hides the children by default so a customer who
  // mashed Submit five times shows up as one row, not five. We keep them
  // visible only when:
  //   1. `include_duplicates: true` is explicit (URL `?include_duplicates=true`),
  //      i.e. the operator chose to see the full audit trail; OR
  //   2. The operator filtered for `processing_status: ['skipped']` — if
  //      they're explicitly asking for skipped rows, hiding the dedup'd
  //      ones would make the filter look broken.
  //
  // Parent rows are never suppressed by this branch (they have an empty
  // `parent_interaction_id` even when `duplicate_submission_count > 0`).
  //
  // IMPORTANT: reply_service.go also sets `parent_interaction_id` on outbound
  // reply interactions to link them back to the inbound they answer. These are
  // NOT duplicates — they carry `processing_status: success` and must remain
  // visible in the Sent view. Only suppress rows that have a parent AND are
  // `skipped`; that is the exclusive signature of a dedup child.
  const operatorAskedForSkipped =
    Array.isArray(filters.processing_status) &&
    filters.processing_status.includes('skipped')
  if (filters.include_duplicates !== true && !operatorAskedForSkipped) {
    result = result.filter((i) => {
      const parentID = i.parent_interaction_id
      if (typeof parentID !== 'string' || parentID === '') return true
      // Has a parent_interaction_id — only hide if it is a dedup child
      // (processing_status === 'skipped'). Outbound replies also carry a
      // parent_interaction_id but are 'success' and must not be suppressed.
      return i.processing_status !== 'skipped'
    })
  }

  return result
}

// ── Helper: Apply client-side sorting ────────────────────────────────────────

function applyClientSort(
  interactions: Interaction[],
  sort: InboxSortState
): Interaction[] {
  if (!sort.column || !sort.direction) return interactions

  // For the two v3-aware columns (sentiment_score, urgency) rows with no
  // value sort to the end regardless of direction so an unsorted block
  // never leads the operator's queue.
  if (sort.column === 'sentiment_score') {
    const dir = sort.direction === 'asc' ? 1 : -1
    return [...interactions].sort((a, b) => {
      const aVal = getSentimentScore(a)
      const bVal = getSentimentScore(b)
      if (aVal === null && bVal === null) return 0
      if (aVal === null) return 1
      if (bVal === null) return -1
      if (aVal === bVal) return 0
      return aVal < bVal ? -dir : dir
    })
  }

  if (sort.column === 'urgency') {
    const dir = sort.direction === 'asc' ? 1 : -1
    return [...interactions].sort((a, b) => {
      const aVal = getUrgencyRank(a)
      const bVal = getUrgencyRank(b)
      if (aVal === null && bVal === null) return 0
      if (aVal === null) return 1
      if (bVal === null) return -1
      if (aVal === bVal) return 0
      return aVal < bVal ? -dir : dir
    })
  }

  const sorted = [...interactions].sort((a, b) => {
    let aVal: string | number | undefined
    let bVal: string | number | undefined

    switch (sort.column) {
      case 'subject':
        aVal = a.subject.toLowerCase()
        bVal = b.subject.toLowerCase()
        break
      case 'received_at':
        aVal = a.received_at
        bVal = b.received_at
        break
      case 'classification':
        aVal = getCategory(a)
        bVal = getCategory(b)
        break
      case 'from':
        aVal = (a.from_name ?? a.from).toLowerCase()
        bVal = (b.from_name ?? b.from).toLowerCase()
        break
      default:
        return 0
    }

    if (aVal === undefined || bVal === undefined) return 0
    if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1
    if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1
    return 0
  })

  return sorted
}

// ── Helper: Optimistic status update ─────────────────────────────────────────

function updateProcessingStatus(
  interactions: Interaction[],
  id: string,
  processing_status: ProcessingStatus
): Interaction[] {
  return interactions.map((i) => (i.id === id ? { ...i, processing_status } : i))
}

// ── Default Inbox Resolution ─────────────────────────────────────────────────

/**
 * The operator inbox always scopes to support@kodeon.ai by default. The
 * canonical `GET /v1/interactions` endpoint requires at least one of
 * `inbox_id`, `received_from`, or `received_to` to avoid a full Firestore
 * collection scan (see services/communications — Task 3506), and the
 * support mailbox is what the Stream inbox surface is actually meant to
 * triage. Operators can still override the inbox filter via the filter
 * bar; this default only applies when no inbox_id has been chosen.
 */
const DEFAULT_INBOX_EMAIL = 'support@kodeon.ai'

function findDefaultInboxId(sources: InboxSource[]): string | undefined {
  return sources.find(
    (s) => s.email.toLowerCase() === DEFAULT_INBOX_EMAIL.toLowerCase(),
  )?.id
}

/**
 * Compute the inbox_id filter list that should be sent to the API.
 *
 * Returns:
 *   - the operator's explicit `inbox_id` selection when set,
 *   - else the support@kodeon.ai inbox id when it can be resolved from
 *     the loaded sources,
 *   - else `undefined` (the backend will fall back to its 90-day default
 *     window).
 */
function resolveInboxIdFilter(
  filterInboxIds: string[] | undefined,
  sources: InboxSource[],
): string[] | undefined {
  if (filterInboxIds && filterInboxIds.length > 0) return filterInboxIds
  const defaultId = findDefaultInboxId(sources)
  return defaultId ? [defaultId] : undefined
}

/**
 * Compute the effective filters to send to the API (Task 4053f).
 *
 * Field-scoped sender lookups (`from_email` / `from_name`) are served by the
 * backend's indexed whole-collection `search_keys` path — but ONLY when no
 * inbox_id is present on the request. Injecting the default support@
 * inbox_id (the normal behaviour) would silently demote the lookup to the
 * per-inbox in-memory path and re-shrink the search scope, so for field
 * lookups we forward only an EXPLICIT operator inbox selection.
 */
function buildEffectiveFilters(
  filters: InboxFilters,
  sources: InboxSource[],
): InboxFilters {
  const fieldLookupActive = !!(filters.from_email || filters.from_name)
  const inboxIds = fieldLookupActive
    ? filters.inbox_id?.length
      ? filters.inbox_id
      : undefined
    : resolveInboxIdFilter(filters.inbox_id, sources)
  return { ...filters, inbox_id: inboxIds }
}

// ── Store Implementation ─────────────────────────────────────────────────────

export const useInboxStore = create<InboxStore>((set, get) => ({
  ...initialState,

  loadList: async () => {
    set({ listLoading: true, listError: null })

    try {
      // Ensure inbox sources are loaded before issuing the list query so we
      // can resolve the default support@kodeon.ai inbox_id. listInboxes
      // memoises via `inboxSourcesLoaded`, so repeat calls are cheap.
      if (!get().inboxSourcesLoaded) {
        await get().loadInboxSources()
      }

      const state = get()
      const effectiveFilters = buildEffectiveFilters(
        state.filters,
        state.inboxSources,
      )

      const response = await listInteractions({
        limit: 100,
        sort: state.sortState,
        filters: effectiveFilters,
        includeReplyPreview: true,
      })

      // Defensive: backend may return data as null when there are no records
      const data = response?.data ?? []
      const filtered = applyClientFilters(data, state.filters)
      const sorted = applyClientSort(filtered, state.sortState)

      set({
        interactions: sorted,
        // Prefer the server's next_cursor: under the 4053f scan loop the
        // resume point is the last SCANNED document, which can be deeper
        // than the last MATCH returned in `data`. Falling back to the last
        // row id keeps compatibility with older backend revisions.
        cursor:
          response?.next_cursor ??
          (data.length > 0 ? data[data.length - 1].id : null),
        total: data.length,
        hasMore: response?.has_more ?? false,
        listLoading: false,
        lastFetchedAt: Date.now(),
        newItemCount: 0,
        loadAllStoppedAtCap: false,
      })
    } catch (error) {
      set({
        listLoading: false,
        listError:
          error instanceof Error ? error.message : 'Failed to load inbox',
      })
    }
  },

  loadMore: async () => {
    const state = get()
    if (state.loadingMore || !state.hasMore || !state.cursor) return

    set({ loadingMore: true })

    try {
      // Mirror loadList: scope the cross-inbox query to support@kodeon.ai
      // by default so the indexed Firestore path is used (unless a
      // field-scoped sender lookup is active — see buildEffectiveFilters).
      const effectiveFilters = buildEffectiveFilters(
        state.filters,
        state.inboxSources,
      )

      const response = await listInteractions({
        limit: 100,
        cursor: state.cursor,
        sort: state.sortState,
        filters: effectiveFilters,
        includeReplyPreview: true,
      })

      // Defensive: backend may return data as null
      const newData = response?.data ?? []
      const allInteractions = [...(state.interactions ?? []), ...newData]
      const filtered = applyClientFilters(allInteractions, state.filters)
      const sorted = applyClientSort(filtered, state.sortState)

      set({
        interactions: sorted,
        // See loadList: the server's next_cursor (last scanned doc) is the
        // correct resume point under the 4053f scan loop.
        cursor:
          response?.next_cursor ??
          (newData.length > 0 ? newData[newData.length - 1].id : null),
        total: allInteractions.length,
        hasMore: response?.has_more ?? false,
        loadingMore: false,
      })
    } catch {
      set({ loadingMore: false })
    }
  },

  loadAllMatches: async () => {
    const MAX_PAGES = 20
    const MAX_ITEMS = 1000

    const state = get()
    if (state.loadingAllMatches || !state.hasMore) return

    set({ loadingAllMatches: true, loadAllStoppedAtCap: false })
    try {
      let pages = 0
      while (
        get().hasMore &&
        pages < MAX_PAGES &&
        get().interactions.length < MAX_ITEMS
      ) {
        const cursorBefore = get().cursor
        await get().loadMore()
        pages += 1
        // Stuck-cursor guard: if the server returned the same resume point
        // (or loadMore bailed early), stop rather than spinning forever.
        if (get().cursor === cursorBefore) break
      }
      if (get().hasMore) {
        set({ loadAllStoppedAtCap: true })
      }
    } finally {
      set({ loadingAllMatches: false })
    }
  },

  setFilters: (filters) => {
    set({ filters })
    get().loadList()
  },

  applyPreset: (presetFilters, options) => {
    // Merge preset facets ON TOP of the operator's current direction/type
    // state. The preset has authority over its own facets (status, urgency,
    // required_action, customer_tone, related_entity_kind, needs_review,
    // …) — not over operator-intent facets (direction, type).
    //
    // The `all` preset (and any other "reset" preset) passes an empty
    // object as its filters; with `resetOtherFacets` we also clear any
    // preset-controlled facets the operator had set manually before
    // selecting the preset. Direction/type still survive untouched.
    const current = get().filters
    const resetOtherFacets = options?.resetOtherFacets ?? true

    // Operator-intent facets that the preset must never touch.
    const operatorBaseline: Pick<InboxFilters, 'direction' | 'type'> = {
      direction: current.direction,
      type: current.type,
    }

    let next: InboxFilters
    if (resetOtherFacets) {
      // Selecting a preset is the operator's request to "swap to this
      // triage view" — non-preset, non-operator facets the operator had
      // previously fiddled with (q, inbox_id, date range, attachment,
      // ad-hoc v3 facet combos) are cleared so the preset state is what
      // the chip says it is. Direction/type survive unconditionally.
      next = { ...operatorBaseline, ...presetFilters }
    } else {
      // Soft-merge mode (not currently used by the UI; reserved for
      // callers that want to stack a preset on top of existing filters
      // without clearing them). Direction/type from the preset are
      // ignored even in this mode.
      next = { ...current, ...presetFilters, ...operatorBaseline }
    }

    set({ filters: next })
    get().loadList()
  },

  setSort: (sortState) => {
    set({ sortState })
    const state = get()
    const sorted = applyClientSort(state.interactions, sortState)
    set({ interactions: sorted })
  },

  clearFilters: () => {
    set({ filters: {} })
    get().loadList()
  },

  loadInboxSources: async () => {
    if (get().inboxSourcesLoaded) return
    try {
      const response = await listInboxes()
      set({ inboxSources: response.data ?? [], inboxSourcesLoaded: true })
    } catch {
      // Non-fatal: inbox filter facet will be empty
    }
  },

  loadAutomations: async () => {
    // Avoid duplicate fetches; the dropdown calls this lazily on first open.
    if (get().automationsLoading || get().automationsLoaded) return

    set({ automationsLoading: true, automationsError: null })
    try {
      // Intentionally omit `enabled` filter: the `enabled` flag controls
      // whether the automation fires *automatically* on events. Paused
      // automations should still be available for manual dispatch — the
      // backend's ExecuteAutomation explicitly bypasses the enabled check.
      const response = await listAutomations({
        triggerEventType: 'interaction.received',
      })
      set({
        automations: response?.data ?? [],
        automationsLoaded: true,
        automationsLoading: false,
      })
    } catch (error) {
      // Non-fatal: dropdown will show an error but the inbox itself stays usable.
      set({
        automationsLoading: false,
        automationsError:
          error instanceof Error ? error.message : 'Failed to load automations',
      })
    }
  },

  triggerAutomation: async (interactionId, automationId) => {
    // Throws on failure so the caller can surface a toast. The backend returns
    // 202 Accepted; the actual execution runs asynchronously and is observable
    // via /v1/executions (not yet wired into the inspector).
    await executeAutomation(automationId, { interaction_id: interactionId })
  },

  bulkTriggerAutomation: async (ids, automationId) => {
    // Schedule each send 3 seconds apart to avoid overloading the backend.
    // Returns immediately so the UI can confirm optimistically — individual
    // failures are logged to the console but not surfaced per-item since
    // the inspector is already dismissed by the time they fire.
    ids.forEach((id, index) => {
      setTimeout(() => {
        executeAutomation(automationId, { interaction_id: id }).catch((err) => {
          console.error(`[bulk-workflow] Failed to dispatch ${id}:`, err)
        })
      }, index * 3000)
    })
  },

  selectInteraction: (id) => {
    set({ selectedId: id, activeTab: 'message' })
    if (id) {
      get().loadInteractionDetail(id)
    }
  },

  toggleSelection: (id) => {
    set((state) => {
      const newSelected = new Set(state.selectedIds)
      if (newSelected.has(id)) {
        newSelected.delete(id)
      } else {
        newSelected.add(id)
      }
      return { selectedIds: newSelected }
    })
  },

  selectAll: () => {
    const allIds = new Set(get().interactions.map((i) => i.id))
    set({ selectedIds: allIds })
  },

  clearSelection: () => {
    set({ selectedIds: new Set() })
  },

  setActiveTab: (tab) => {
    set({ activeTab: tab })
  },

  loadInteractionDetail: async (id) => {
    const cached = get().detailCache.get(id)
    if (cached && Date.now() - cached.lastFetchedAt < 30000) {
      return
    }

    set({ detailLoading: true, detailError: null })

    try {
      // Fetch thread messages and full asset metadata in parallel.
      // The backend returns attachments on messages as plain asset ID strings
      // (e.g. ["file_abc"]) rather than rich objects. We fetch the full File
      // records from /v1/interactions/{id}/assets and map them back onto
      // each message so the UI has filename, size, content_type, and url.
      const [messages, assetsResult] = await Promise.all([
        fetchThreadMessages(id),
        getInteractionAssets(id).catch(() => ({ data: [] as MessageAttachment[] })),
      ])

      // Enrich each message's attachments: replace raw ID strings with
      // full MessageAttachment objects where available.
      const enrichedMessages = enrichMessageAttachments(messages, assetsResult.data)

      const interaction = get().interactions.find((i) => i.id === id)

      if (interaction) {
        set((state) => {
          const newCache = new Map(state.detailCache)
          newCache.set(id, {
            interaction,
            messages: enrichedMessages,
            lastFetchedAt: Date.now(),
          })
          return { detailCache: newCache, detailLoading: false }
        })
      } else {
        set({ detailLoading: false, detailError: 'Interaction not found' })
      }
    } catch (error) {
      set({
        detailLoading: false,
        detailError:
          error instanceof Error
            ? error.message
            : 'Failed to load interaction',
      })
    }
  },

  loadHistoryInteractionDetail: async (interaction) => {
    const id = interaction.id

    // Cache hit within the same 30s TTL used by loadInteractionDetail.
    const cached = get().detailCache.get(id)
    if (cached && Date.now() - cached.lastFetchedAt < 30000) {
      return
    }

    try {
      // Fetch the thread + assets in parallel — same enrichment pipeline as
      // loadInteractionDetail so attachment chips render with full metadata.
      const [messages, assetsResult] = await Promise.all([
        fetchThreadMessages(id),
        getInteractionAssets(id).catch(() => ({ data: [] as MessageAttachment[] })),
      ])

      const enrichedMessages = enrichMessageAttachments(messages, assetsResult.data)

      set((state) => {
        const newCache = new Map(state.detailCache)
        newCache.set(id, {
          interaction,
          messages: enrichedMessages,
          lastFetchedAt: Date.now(),
        })
        return { detailCache: newCache }
      })
    } catch (error) {
      // Re-throw so the caller (the history row) can surface a retry-able
      // inline error. Unlike loadInteractionDetail this does NOT mutate
      // detailLoading/detailError — those belong to the selected interaction
      // (Message tab) and must not be clobbered by a history expansion.
      throw error instanceof Error
        ? error
        : new Error('Failed to load message body')
    }
  },

  setInteractionStatus: async (id, status) => {
    // Capture current status for revert
    const prev = get().interactions.find((i) => i.id === id)?.processing_status ?? 'pending'
    // Optimistic update
    set((state) => ({
      interactions: updateProcessingStatus(state.interactions, id, status),
    }))
    try {
      await apiUpdateProcessingStatus(id, status)
    } catch {
      // Revert on failure
      set((state) => ({
        interactions: updateProcessingStatus(state.interactions, id, prev),
      }))
    }
  },

  bulkSetStatus: async (ids, status) => {
    // Optimistic update
    set((state) => ({
      interactions: state.interactions.map((i) =>
        ids.includes(i.id) ? { ...i, processing_status: status } : i
      ),
    }))
    try {
      await Promise.all(ids.map((id) => apiUpdateProcessingStatus(id, status)))
    } catch {
      // Revert: reload list
      get().loadList()
    }
  },

  bulkArchive: async (ids) => {
    // Optimistic update
    set((state) => ({
      interactions: state.interactions.map((i) =>
        ids.includes(i.id) ? { ...i, processing_status: 'success' as ProcessingStatus } : i
      ),
    }))
    try {
      await batchArchiveInteractions({ interaction_ids: ids, archive: true })
    } catch {
      // Revert: reload list
      get().loadList()
    }
  },

  saveCaseNotes: async (id, notes) => {
    await updateCaseNotes(id, { notes })
    set((state) => {
      const newCache = new Map(state.detailCache)
      const cached = newCache.get(id)
      if (cached) {
        newCache.set(id, { ...cached, caseNotes: notes })
      }
      return { detailCache: newCache }
    })
  },

  reply: async (request) => {
    const message = await replyToMessage(request)
    // Update the interaction status to 'replied' optimistically
    set((state) => ({
      interactions: state.interactions.map((i) =>
        i.id === request.interaction_id
          ? { ...i, processing_status: 'success' as ProcessingStatus }
          : i
      ),
      detailCache: (() => {
        const newCache = new Map(state.detailCache)
        const cached = newCache.get(request.interaction_id)
        if (cached) {
          newCache.set(request.interaction_id, {
            ...cached,
            messages: [...(cached.messages ?? []), message],
            interaction: { ...cached.interaction, processing_status: 'success' as ProcessingStatus },
          })
        }
        return newCache
      })(),
    }))
    return message
  },

  incrementNewItems: (count = 1) => {
    set((state) => ({ newItemCount: state.newItemCount + count }))
  },

  clearNewItems: () => {
    set({ newItemCount: 0 })
  },

  hydrateFromUrl: (searchParams) => {
    const filters: InboxFilters = {}

    const q = searchParams.get('q')
    if (q) filters.q = q

    // Field-scoped sender lookups (Task 4053f). Normalized lowercase to
    // match the backend's search_keys normalization.
    const fromEmail = searchParams.get('from_email')
    if (fromEmail) filters.from_email = fromEmail.trim().toLowerCase()

    const fromName = searchParams.get('from_name')
    if (fromName) filters.from_name = fromName.trim().toLowerCase()

    // Default to 'inbound' on first load when no direction param is present in
    // the URL. The filter bar exposes an explicit "All" preset that clears
    // direction; users who want to see outbound/both can pick it.
    const direction = searchParams.get('direction')
    if (direction) {
      filters.direction = direction as InteractionDirection
    } else if (!searchParams.has('direction')) {
      filters.direction = 'inbound'
    }

    // Inbox is email-only by default. URL param `type=email,chat` allows
    // override (e.g. for future combined surfaces); explicit empty value
    // (`type=`) means "all channels including reviews".
    const typeParam = searchParams.get('type')
    if (typeParam !== null) {
      filters.type = typeParam ? (typeParam.split(',') as InboxFilters['type']) : undefined
    } else if (!searchParams.has('type')) {
      filters.type = ['email']
    }

    const processingStatus = searchParams.get('processing_status')
    if (processingStatus)
      filters.processing_status = processingStatus.split(',') as ProcessingStatus[]


    const inboxId = searchParams.get('inbox_id')
    if (inboxId) filters.inbox_id = inboxId.split(',')

    const attachment = searchParams.get('attachment')
    if (attachment) filters.attachment = attachment === 'true'

    const receivedFrom = searchParams.get('received_from')
    if (receivedFrom) filters.received_from = receivedFrom

    const receivedTo = searchParams.get('received_to')
    if (receivedTo) filters.received_to = receivedTo

    // ── v3 Classification facets ───────────────────────────────────────
    const urgency = searchParams.get('urgency')
    if (urgency)
      filters.urgency = urgency.split(',') as InboxFilters['urgency']

    const requiredAction = searchParams.get('required_action')
    if (requiredAction)
      filters.required_action = requiredAction.split(
        ',',
      ) as InboxFilters['required_action']

    const tone = searchParams.get('customer_tone')
    if (tone)
      filters.customer_tone = tone.split(',') as InboxFilters['customer_tone']

    const entityKind = searchParams.get('related_entity_kind')
    if (entityKind)
      filters.related_entity_kind = entityKind.split(
        ',',
      ) as InboxFilters['related_entity_kind']

    const needsReview = searchParams.get('needs_review')
    if (needsReview === 'true') filters.needs_review = true

    // Dedup escape hatch — `?include_duplicates=true` opts back in to
    // seeing the rows the inbox suppresses by default (Task 3510).
    const includeDuplicates = searchParams.get('include_duplicates')
    if (includeDuplicates === 'true') filters.include_duplicates = true

    const sortColumn = searchParams.get('sort') as InboxSortState['column']
    const sortDir = searchParams.get('dir') as 'asc' | 'desc' | null
    const sortState: InboxSortState = {
      column: sortColumn ?? 'received_at',
      direction: sortDir ?? 'desc',
    }

    const selected = searchParams.get('selected')

    set({
      filters,
      sortState,
      selectedId: selected,
    })
  },

  serializeToUrl: () => {
    const state = get()
    const params = new URLSearchParams()

    if (state.filters.q) params.set('q', state.filters.q)
    if (state.filters.from_email)
      params.set('from_email', state.filters.from_email)
    if (state.filters.from_name)
      params.set('from_name', state.filters.from_name)
    if (state.filters.direction)
      params.set('direction', state.filters.direction)
    if (state.filters.type?.length)
      params.set('type', state.filters.type.join(','))
    if (state.filters.processing_status?.length)
      params.set('processing_status', state.filters.processing_status.join(','))
    if (state.filters.classification?.length)
      params.set('classification', state.filters.classification.join(','))
    if (state.filters.inbox_id?.length)
      params.set('inbox_id', state.filters.inbox_id.join(','))
    if (state.filters.attachment !== undefined)
      params.set('attachment', String(state.filters.attachment))
    if (state.filters.received_from)
      params.set('received_from', state.filters.received_from)
    if (state.filters.received_to)
      params.set('received_to', state.filters.received_to)
    if (state.filters.urgency?.length)
      params.set('urgency', state.filters.urgency.join(','))
    if (state.filters.required_action?.length)
      params.set('required_action', state.filters.required_action.join(','))
    if (state.filters.customer_tone?.length)
      params.set('customer_tone', state.filters.customer_tone.join(','))
    if (state.filters.related_entity_kind?.length)
      params.set(
        'related_entity_kind',
        state.filters.related_entity_kind.join(','),
      )
    if (state.filters.needs_review === true)
      params.set('needs_review', 'true')

    // Dedup override — only serialise when explicitly on, so the URL stays
    // clean for the default-suppressed view.
    if (state.filters.include_duplicates === true)
      params.set('include_duplicates', 'true')

    if (state.sortState.column) params.set('sort', state.sortState.column)
    if (state.sortState.direction) params.set('dir', state.sortState.direction)

    if (state.selectedId) params.set('selected', state.selectedId)

    return params
  },
}))

// ── Selectors ────────────────────────────────────────────────────────────────

/**
 * Select current interaction detail from cache.
 */
export const selectCurrentDetail = (
  state: InboxStore
): InteractionDetail | null => {
  if (!state.selectedId) return null
  return state.detailCache.get(state.selectedId) ?? null
}

/**
 * Count unread interactions in current list.
 */
export const selectUnreadCount = (state: InboxStore): number =>
  state.interactions.filter((i) => !['success', 'skipped'].includes(i.processing_status)).length

/**
 * Check if any filters are active.
 */
export const selectHasActiveFilters = (state: InboxStore): boolean => {
  const f = state.filters
  return !!(
    f.q ||
    f.from_email ||
    f.from_name ||
    f.direction ||
    f.processing_status?.length ||
    f.classification?.length ||
    f.inbox_id?.length ||
    f.attachment !== undefined ||
    f.received_from ||
    f.received_to ||
    f.urgency?.length ||
    f.required_action?.length ||
    f.customer_tone?.length ||
    f.related_entity_kind?.length ||
    f.needs_review === true
  )
}

/**
 * Active search mode (Task 4053f).
 *
 * - `field`: an exact, indexed sender lookup (`from_email` / `from_name`)
 *   is active — whole-inbox scope, no 90-day window.
 * - `fuzzy`: a free-text `q` search is active — server scans the last 90
 *   days (or the operator's explicit window) and the client refines.
 * - `null`: no search active.
 */
export type InboxSearchMode = 'field' | 'fuzzy' | null

export const selectSearchMode = (state: InboxStore): InboxSearchMode => {
  if (state.filters.from_email || state.filters.from_name) return 'field'
  if (state.filters.q) return 'fuzzy'
  return null
}
